import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Contact: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  // Function to generate WhatsApp URL with form data
  const generateWhatsAppUrl = (formData: typeof formState) => {
    const phoneNumber = '919652425181'; // WhatsApp number without + sign
    const message = `New enquiry from website:
Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company || 'Not provided'}
Message: ${formData.message}`;

    return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Generate WhatsApp URL with form data
    const whatsappUrl = generateWhatsAppUrl(formState);

    // Simulate form submission and then open WhatsApp
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);

      // Open WhatsApp in a new tab
      window.open(whatsappUrl, '_blank');

      // Reset form
      setFormState({
        name: '',
        email: '',
        company: '',
        message: '',
      });
    }, 1500);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    },
  };

  const socialLinks = [
    {
      name: 'LinkedIn',
      icon: 'LinkedIn',
      url: 'https://linkedin.com/company/atomcx-ai',
    },
    {
      name: 'Twitter',
      icon: 'Twitter',
      url: 'https://twitter.com/atomcx_ai',
    },
    {
      name: 'GitHub',
      icon: 'GitHub',
      url: 'https://github.com/atomcx-ai',
    }
  ];

  return (
    <section id="contact" className="section-padding bg-deep-blue relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute left-0 top-1/4 w-64 h-64 bg-electric-blue/10 rounded-full blur-3xl"></div>
      <div className="absolute right-0 bottom-1/3 w-80 h-80 bg-teal-accent/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold mb-6">
            Get in <span className="gradient-text">Touch</span>
          </motion.h2>

          <motion.p variants={itemVariants} className="text-lg max-w-3xl mx-auto text-gray-300">
            Ready to explore how our AI agents can transform your business? Reach out to us for a consultation 
            or to schedule a demo of our intelligent automation solutions.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Form */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="bg-dark-blue/50 p-8 rounded-xl border border-electric-blue/30 shadow-neon"
          >
            <motion.h3 variants={itemVariants} className="text-2xl font-bold mb-6">
              Send us a message
            </motion.h3>

            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-electric-blue to-teal-accent rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-deep-blue text-2xl font-bold">✓</span>
                </div>
                <h4 className="text-xl font-bold mb-2">Thank you for reaching out!</h4>
                <p className="text-gray-300 mb-2">
                  Your message has been sent to our WhatsApp.
                </p>
                <p className="text-gray-300">
                  We'll get back to you shortly.
                </p>
              </motion.div>
            ) : (
              <motion.form variants={itemVariants} onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-deep-blue border border-electric-blue/30 rounded-md focus:outline-none focus:ring-2 focus:ring-electric-blue text-white"
                      placeholder=""
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formState.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-deep-blue border border-electric-blue/30 rounded-md focus:outline-none focus:ring-2 focus:ring-electric-blue text-white"
                      placeholder=""
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formState.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-deep-blue border border-electric-blue/30 rounded-md focus:outline-none focus:ring-2 focus:ring-electric-blue text-white"
                    placeholder="Your Company"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formState.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-deep-blue border border-electric-blue/30 rounded-md focus:outline-none focus:ring-2 focus:ring-electric-blue text-white resize-none"
                    placeholder="Tell us about your project or inquiry..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 py-4 bg-gradient-to-r from-electric-blue to-teal-accent rounded-md text-deep-blue font-bold text-lg shadow-neon hover:shadow-neon-lg transition-all"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </motion.form>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="bg-dark-blue/50 p-8 rounded-xl border border-electric-blue/30"
          >
           {/* <motion.h3 variants={itemVariants} className="text-2xl font-bold mb-6">
              Connect with us
            </motion.h3>

            <motion.div variants={itemVariants} className="mb-8">
              <h4 className="text-xl font-semibold mb-4">Our Location</h4>
              <p className="text-gray-300 mb-2">
                123 AI Innovation Center
              </p>
              <p className="text-gray-300 mb-2">
                San Francisco, CA 94105
              </p>
              <p className="text-gray-300">
                United States
              </p>
            </motion.div>*/}

            <motion.div variants={itemVariants} className="mb-8">
              <h4 className="text-xl font-semibold mb-4">Contact Information</h4>
              <p className="text-gray-300 mb-2">
                <span className="text-electric-blue">Email:</span> agent@atomcx.ai
              </p>
              <p className="text-gray-300">
                <span className="text-electric-blue">Phone:</span> +91 9652425181
              </p>
            </motion.div>

   {/*         <motion.div variants={itemVariants}>
              <h4 className="text-xl font-semibold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                {socialLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-deep-blue rounded-full flex items-center justify-center border border-electric-blue/30 hover:bg-electric-blue/20 transition-colors"
                  >
                    <span className="text-electric-blue">{link.icon}</span>
                  </a>
                ))}
              </div>
            </motion.div>*/}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
